import {
  Button, Chip, DialogActions, DialogContent, DialogTitle, Grid
} from '@material-ui/core';
import { ASBV } from 'api/query/asbv/asbv';
import ASBVApi from 'api/query/asbv/asbv-api';
import { ASBVCategory } from 'api/query/asbv/asbv-category.enum';
import { Autocomplete } from 'components/dropdown/autocomplete/autocomplete';
import { DropdownItem } from 'components/dropdown/dropdown-item';
import ModalCloseButton from 'components/modal-close-button';
import ResetButton from 'components/reset-button';
import { AppNumConstants } from 'constants/app-num-constants.enum';
import deepEqual from 'deep-equal';
import { ASBVsFilterValue } from 'pages/search/asbvs/asbvs-filter-value';
import { useFilters } from 'pages/search/filters/use-filters';
import { usePrime } from 'pages/search/prime/use-prime';
import { useSearch } from 'pages/search/use-search';
import { useResults } from 'pages/shared/use-results';
import React, {
  memo, useCallback, useEffect, useState
} from 'react';
import { useNavigation } from 'react-navi';
import { getASBVsFromFilters, getCustomASBVsFromStorage, saveCustomASBVsToStorage } from 'utils/asbv-utils';
import { useBreakpoints } from 'utils/use-breakpoints';
import { CustomiseASBVsProps } from './customise-asbvs-props';
import { getASBVColumns } from './customise-asbvs-utils';
import './customise-asbvs.scss';

/**
 * Allows customising the ASBVs displayed with the search results.
 *
 * If an ASBV was selected as a filter, it will appear here. Otherwise the list
 * will be populated by the "default" ASBVs, as defined by the asbv.category prop.
 *
 *
 * @param props
 */
export const CustomiseASBVs: React.FC<CustomiseASBVsProps> = memo((props) => {
  const {
    onCancel,
    onSave,
    hideNonPercentileASBVS = false,
  } = props;

  const title = 'Customise your ASBVs, index and data display';
  const { db, traits } = usePrime();
  const { filters, updateASBVsFilters } = useFilters();
  const { customASBVs, setCustomASBVs } = useResults();
  const { setCurrentFilters } = useSearch();
  const { phone } = useBreakpoints();

  const navigation = useNavigation();

  const [asbv] = useState<Map<string, ASBVsFilterValue>>(filters.asbv);
  const [asbvs, setASBVs] = useState<ASBV[]>([]);
  const [selectedASBVs, setSelectedASVBs] = useState<ASBV[]>([]);
  const [defaultASBVs, setDefaultASBVs] = useState<ASBV[]>([]);
  const [items, setItems] = useState<DropdownItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<DropdownItem[]>([]);
  const [resetDisabled, setResetDisabled] = useState<boolean>(true);
  const [columns, setColumns] = useState<boolean[]>(getASBVColumns());

  const defaultMinCols = 8;

  /**
   * Sets the visibility of the columns.
   *
   * Always display columns 1-8
   *
   * Display columns 9-12 IF:
   * - Default ASBVs is more than 8
   * - Selected ASBVs is more than 8
   */
  const setColumnVisibility = useCallback(() => {
    let changed = false;
    const cols = [...columns];
    for (let i = 0; i < cols.length; i += 1) {
      const isVisible = i < defaultMinCols || selectedASBVs.length > defaultMinCols;
      if (cols[i] !== isVisible) {
        changed = true;
      }
      cols[i] = isVisible;
    }
    if (changed) {
      setColumns(cols);
    }
  }, [columns, selectedASBVs.length]);

  /**
   * Sets the selected ASBVs based on a list of dropdown items.
   *
   * This occurs when a value is selected/deselected from the autocomplete dropdown.
   * @param dropdownItems
   */
  const setSelectedASBVsByItems = (dropdownItems: DropdownItem[]) => {
    const selected = dropdownItems.map((i) => asbvs.find((a) => a.asbvId === i.id) as ASBV);

    setSelectedASVBs(selected);
  };

  /**
   * Sets the selected items (in the dropdown) by the list of ASBVs.
   */
  const setSelectedItemsByASBVs = useCallback((chips: ASBV[]) => {
    // Gets the selected based on being a default
    const selected = items.filter((item) => chips.find((c) => c.asbvId === item.id));
    setSelectedItems(selected);
  }, [items]);

  /**
   * Removes a single item from the ASBVs.
   * @param asbvItem
   * @param asbvItems
   */
  const removeItem = (asbvItem: DropdownItem, asbvItems: DropdownItem[]) => {
    asbvItems.splice(asbvItems.indexOf(asbvItem), 1);
  };

  /**
   * Gets the default ASBVs based on the category.
   */
  const getDefaultASBVs = useCallback((data: ASBV[]) => data.filter((a) => a.category === ASBVCategory.Default).filter((a) => (hideNonPercentileASBVS ? a.topPercentileDirection !== 'NA' : true)),
    [hideNonPercentileASBVS]);

  /**
   * Gets the cached ASBV ids from local storage and returns a list of ASBVs.
   */
  const getCacheASBVs = useCallback(() => {
    const cacheASBVs = getCustomASBVsFromStorage(db.id, traits.id);
    return cacheASBVs;
  }, [db.id, traits.id]);

  /**
   * Gets the initial state ASBV list.
   *
   * This first retrieves the cached ASBVs from local storage then
   * sets the initial ASBVs based on selected filters.
   */
  const getASBVs = useCallback(() => {
    const cacheASBVs = getCacheASBVs();
    let source = defaultASBVs;
    if (cacheASBVs?.length) {
      source = cacheASBVs;
    }
    const filterASBVs = getASBVsFromFilters(asbvs, asbv, source, AppNumConstants.MaxASBVs);

    return filterASBVs;
  }, [asbv, asbvs, defaultASBVs, getCacheASBVs]);

  /**
   * Updates the selected ASBVs.
   *
   * If the item was removed, it is simply removed from the selected list.
   *
   * If an item was added, push it to the end.
   * @param selectedASBVItems
   * @param changedASBV
   * @param reason
   */
  const updateSelectedASBVs = (selectedASBVItems: DropdownItem[], changedASBV: DropdownItem, reason: 'Add' | 'Remove' | undefined) => {
    const [...selected] = selectedItems;
    switch (reason) {
      case 'Remove':
        removeItem(changedASBV, selected);
        break;
      case 'Add':
        selected.push(changedASBV);
        break;
      default:
        break;
    }

    setSelectedItems(selected);
    setSelectedASBVsByItems(selected);
  };

  /**
   * Removes an ASBV.
   * @param asbvId
   */
  const deleteChip = (asbvId: string) => {
    const item = selectedItems.find((i) => i.id === asbvId)!;
    selectedItems.splice(selectedItems.indexOf(item), 1);
    setSelectedItems(selectedItems);
    setSelectedASBVsByItems(selectedItems);
  };

  /**
   * Resets to the default ASBVs.
   */
  const reset = () => {
    // Gets the defaults based on the category
    const defaults = getDefaultASBVs(asbvs);
    setSelectedASVBs(defaults);
    setSelectedItemsByASBVs(defaults);
  };

  /**
   * Saves the custom ASBVs to localstorage and updates the results context
   * then fires the onSave callback to close the modal.
   */
  const save = () => {
    saveCustomASBVsToStorage(selectedASBVs, db.id, traits.id);

    // // Delete any ASBV from the filters if it was removed here.
    const asbvFilters = new Map(asbv);
    let filtersUpdated = false;
    asbvFilters.forEach((value) => {
      if (!selectedASBVs.find((a) => a.asbvId.toString() === value.asbv)) {
        asbvFilters.delete(value.asbv);
        filtersUpdated = true;
      }
    });

    if (filtersUpdated) {
      // Update both the filters and the current filters.
      // Current filters are used to get the search results.
      updateASBVsFilters(asbvFilters);
      setCurrentFilters({
        ...filters,
        asbv: asbvFilters,
      });

      // If the asbvIds param was provided from a Recommended Search,
      // remove the asbvIds so the search can use the new custom ASBVs.
      navigation.getCurrentValue().url.query.asbvIds = '';
    }

    if (!deepEqual(selectedASBVs, customASBVs)) {
      setCustomASBVs(selectedASBVs);
    }

    onSave();
  };

  useEffect(() => {
    const getASBVsGroup = async () => {
      const data = await ASBVApi.getGroup(db.id, traits.id);
      // If we are hiding nonPercentileASBVs then filter them out by TopPercentileDirection == 'NA'
      setASBVs(data.filter((a) => (hideNonPercentileASBVS ? a.topPercentileDirection !== 'NA' : true)));

      const dropdownItems: DropdownItem[] = data.filter((a) => (hideNonPercentileASBVS ? a.topPercentileDirection !== 'NA' : true)).map((a) => ({
        id: a.asbvId,
        value: `${a.label} (${a.abbrev})`,
        group: {
          id: a.category,
          label: a.category === ASBVCategory.All ? 'All other ASBVs' : 'Default ASBV & Index based on trait',
        },
      }));
      setItems(dropdownItems);

      // Gets the defaults based on the category
      const defaults = getDefaultASBVs(data);
      setDefaultASBVs(defaults);
      setSelectedASVBs(defaults);
    };
    if (db.id) {
      getASBVsGroup();
    }
  }, [db.id, getDefaultASBVs, hideNonPercentileASBVS, traits.id]);

  /**
   * Updates the selected ASBVs (chips) to include any ASBV selected in the filters.
   *
   * This also updates the selected items (dropdown items) to match the newly selected ASBVs.
   */
  useEffect(() => {
    if (defaultASBVs.length) {
      const chips = getASBVs();
      setSelectedASVBs(chips);

      // Gets the selected based on being a default
      setSelectedItemsByASBVs(chips);
    }
  }, [defaultASBVs, getASBVs, items, setSelectedItemsByASBVs]);

  /**
   * Sets the visibility of the columns.
   */
  useEffect(() => {
    setColumnVisibility();
  }, [selectedASBVs, setColumnVisibility]);

  /**
   * Sets the enabled state of the reset to defaults button.
   */
  useEffect(() => {
    setResetDisabled(deepEqual(selectedASBVs, defaultASBVs));
  }, [defaultASBVs, selectedASBVs]);

  return (
    <div className="sg-customise-asbvs">
      <DialogTitle className="customise-asbvs__title">
        {phone && <h5>{title}</h5>}
        {!phone && (
          <>
            <h2>{title}</h2>
            <ResetButton
              disabled={resetDisabled}
              onClick={reset}
            >
              Reset to default ASBVs
            </ResetButton>
          </>
        )}
      </DialogTitle>
      <ModalCloseButton onClose={() => onCancel()} />
      <DialogContent>
        <div className="chips__disclaimer">A maximum of 20 columns can be selected. Click the 'X' to remove the ASBV or Index from a column.</div>
        <div className="chips__table">
          {columns.map((column, index) => (
            <React.Fragment key={`Column-${index + 1}`}>
              {column && (
                <div className="chip__col">
                  <div className="chip__head">{!phone && 'Column'} {index + 1}</div>
                  <div className="chip__cell">
                    {selectedASBVs[index] && (
                      <Chip
                        className="asbv__chip"
                        label={selectedASBVs[index].abbrev}
                        onDelete={() => deleteChip(selectedASBVs[index].asbvId)}
                      />
                    )}
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
          {!phone && selectedASBVs.length > 8 && (
            <div className="table__scroll-disclaimer">These columns will appear as you scroll to the right</div>
          )}
        </div>
        {/** Reset button - phone only */}
        {phone && (
          <div className="asbvs__reset">
            <ResetButton
              variant="button"
              disabled={resetDisabled}
              onClick={reset}
            >
              Reset to default ASBVs
            </ResetButton>
          </div>
        )}
        <Grid
          className="asbvs__dropdown-container"
          container
          item
          sm={8}
        >
          {!!items.length && (
            <Autocomplete
              multiple
              items={items}
              item={selectedItems}
              disabled={selectedASBVs.length === 20}
              label="ASBV OR INDEX"
              placeholder="Search for ASBV or Index"
              placement={phone ? 'top' : 'bottom'}
              onSelectionChange={(selected, changed, reason) => updateSelectedASBVs(selected, changed as DropdownItem, reason)}
            />
          )}
        </Grid>
      </DialogContent>
      <DialogActions className="customise-asbvs__actions">
        <Button
          variant="outlined"
          onClick={() => onCancel()}
        >
          Cancel
        </Button>
        <Button
          onClick={() => save()}
          disabled={selectedASBVs.length === 0}
        >
          Save
        </Button>
      </DialogActions>
    </div>
  );
});

// (CustomiseASBVs as any).whyDidYouRender = true;
